import PropTypes from 'prop-types';
import { captureException } from '@sentry/react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { URL_API } from '../../constants';
import { chatgenieAPI } from '../../services/chatgenieAPI';
import Firebase from './Firebase';
import { noop } from '..';

const FirebaseContext = createContext({});

export default function FirebaseProvider({ children, config }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [firebaseInstance] = useState(() => new Firebase(config));
  const { memberId } = useSelector(state => state.users.user);

  const defaultFilterQuery = [
    {
      key: 'memberId',
      operator: '==',
      value: memberId
    }
  ];

  const getDocuments = ({ filters = [], withDefaultFilter = true, ...rest }) =>
    firebaseInstance.getDocuments({
      filters: withDefaultFilter ? [...defaultFilterQuery, ...filters] : filters,
      ...rest
    });
  const onCollectionChange = ({ filters = [], withDefaultFilter = true, ...rest }) =>
    firebaseInstance.onCollectionChange({
      filters: withDefaultFilter ? [...defaultFilterQuery, ...filters] : filters,
      ...rest
    });
  const signInWithCustomToken = async () => {
    try {
      return chatgenieAPI
        .instance({
          url: `${URL_API}/api/oauth/firebase/token`,
          method: 'GET'
        })
        .then(res => firebaseInstance.signInWithCustomToken(res?.data?.data));
    } catch (err) {
      captureException(err, {
        extra: {
          operation: 'signin'
        },
        tags: {
          service: 'firebase'
        }
      });
      throw err;
    }
  };
  const createDocument = (data, options, customId) => {
    const useId = customId || { memberId };
    const validated = omitBy(data, isUndefined);
    return firebaseInstance.createDocument({ ...validated, ...useId }, options);
  };
  const updateDocument = (data, options, customId) => {
    const useId = customId || { memberId };
    const validated = omitBy(data, isUndefined);
    return firebaseInstance.updateDocument({ ...validated, ...useId }, options);
  };

  useEffect(() => {
    if (!memberId) {
      return noop;
    }

    const unsubscriber = firebaseInstance.subscribeAuthStateChange(user => {
      if (user && memberId && user.uid !== String(memberId)) {
        firebaseInstance.signOut();
        setCurrentUser(null);
      } else {
        setCurrentUser(user);
      }
    });

    return unsubscriber;
  }, [memberId]);

  return (
    <FirebaseContext.Provider
      value={{
        ...firebaseInstance,
        getDocuments,
        onCollectionChange,
        createDocument,
        signInWithCustomToken,
        updateDocument,
        currentUser,
        isLoggedIn: !!currentUser
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
}
FirebaseProvider.propTypes = {
  children: PropTypes.node.isRequired,
  config: PropTypes.shape({
    apiKey: PropTypes.string,
    authDomain: PropTypes.string,
    databaseURL: PropTypes.string,
    projectId: PropTypes.string,
    storageBucket: PropTypes.string,
    messagingSenderId: PropTypes.string,
    appId: PropTypes.string
  }).isRequired
};
export const useFirebase = () => useContext(FirebaseContext);

export const useFirebaseSubscriptions = ({ subscriptions = [], deps = [] }) => {
  const { currentUser, onCollectionChange } = useFirebase();
  useEffect(() => {
    if (!currentUser) {
      return noop;
    }
    let unsubscribers = [];
    subscriptions.forEach(subscription => {
      onCollectionChange(subscription)
        .then(unsubscribeFn => {
          unsubscribers.push(unsubscribeFn);
        })
        .catch(err => {
          console.error('Subscription failed: ', err);
        });
    });

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
      unsubscribers = [];
    };
  }, [currentUser, ...deps]);
};
